import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

const currencySymbol = (c) => {
  const map = { USD: "$", INR: "₹", EUR: "€", AED: "د.إ", GBP: "£" };
  return map[c] || "";
};

// --- canonical labels ---
const FACILITY_MAP = {
  whatsapp: "WhatsApp",
  payments: "Payments",
  internal_chats: "Internal Chats",
  chatbots: "Chatbots",
  ai_scenarios: "AI Scenarios",
};

// normalize a single token to our canonical key
const normalizeFacility = (s) => {
  if (!s) return "";
  let v = String(s).trim();
  if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
    v = v.slice(1, -1).trim();
  }
  v = v.toLowerCase().replace(/\s+/g, "_");
  if (v === "internal_chat") v = "internal_chats";
  if (v === "ai_senerious" || v === "ai_scenarious" || v === "ai_scenario") v = "ai_scenarios";
  return FACILITY_MAP[v] ? v : "";
};

// parse facilities from array / JSON string / Postgres text[] / CSV
const parseFacilities = (raw) => {
  if (Array.isArray(raw)) return raw.map(normalizeFacility).filter(Boolean);
  if (raw == null) return [];
  const s = String(raw).trim();

  // JSON array string
  if (s.startsWith("[")) {
    try {
      const arr = JSON.parse(s);
      return Array.isArray(arr) ? arr.map(normalizeFacility).filter(Boolean) : [];
    } catch {}
  }

  // Postgres text[]: {"a","b with space"}
  if (s.startsWith("{") && s.endsWith("}")) {
    const quoted = [...s.matchAll(/"([^"]*)"/g)].map((m) => m[1]);
    if (quoted.length) return quoted.map(normalizeFacility).filter(Boolean);
    const inner = s.slice(1, -1);
    if (!inner) return [];
    return inner.split(",").map(normalizeFacility).filter(Boolean);
  }

  // CSV: a,b
  return s.split(",").map(normalizeFacility).filter(Boolean);
};

const PackagesView = () => {
  const { data: id } = useParams();
  const navigate = useNavigate();

  const [rowData, setRowData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setErr("");

    fetch(`${process.env.REACT_APP_API_URL}/packages/${id}`, {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        authorization:
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImFkbWluQGF1dGhmYXN0LmNvbSIsImlkIjo1LCJ1c2VyVHlwZSI6InN1cGVyYWRtaW4iLCJpYXQiOjE2NjM2NTE2NzV9.raQf8JTVyiM51zg6OgH1RLkQqK_QyGPO2rJa9-oYDko",
      }),
    })
      .then((r) => r.json())
      .then((resp) => setRowData(resp?.data || null))
      .catch(() => setErr("Failed to load package details."))
      .finally(() => setLoading(false));
  }, [id]);

  const priceAmt = rowData?.price_amount ?? "";
  const priceCur = rowData?.price_currency ?? "";
  const priceText =
    priceAmt === "" ? "-" : `${currencySymbol(priceCur)}${priceAmt} ${priceCur}`;

  const wsMode = rowData?.workspace_mode || "-";
  const wsLimit =
    rowData?.workspace_mode === "limited" ? rowData?.workspace_limit ?? "-" : "-";

  const durationLabel = rowData?.duration_label || "-";
  const durationDays = rowData?.duration_in_days
    ? ` (${rowData.duration_in_days} days)`
    : "";

  const facilitiesKeys = parseFacilities(rowData?.facilities);
  const hasFacilities = facilitiesKeys.length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar: Back on left, title centered */}
      <div className="w-full bg-blue-600 text-white">
        <div className="relative max-w-5xl mx-auto px-4 py-3 flex items-center justify-center">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 inline-flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm font-medium"
            type="button"
          >
            ← Back
          </button>
          <h1 className="text-lg md:text-xl font-semibold">View Package Details</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10">
        {err ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
            {err}
          </div>
        ) : loading ? (
          <div className="text-center text-gray-600">Loading package details…</div>
        ) : !rowData ? (
          <div className="text-center text-gray-600">Package not found.</div>
        ) : (
          <div className="mx-auto w-full max-w-lg rounded-2xl bg-white shadow-md ring-1 ring-black/5">
            <div className="px-6 py-5 border-b">
              <h2 className="text-xl font-semibold text-gray-900">
                {rowData?.name || "Package"}
              </h2>
              <p className="text-sm text-gray-500">ID: {rowData?.id ?? "-"}</p>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Basics */}
              <div className="grid grid-cols-1 gap-4">
                <Row label="Type" value={rowData?.type || "-"} />
                <Row label="Workspaces Mode" value={wsMode} />
                {rowData?.workspace_mode === "limited" && (
                  <Row label="Workspace Limit" value={wsLimit} />
                )}
                <Row
                  label="Users"
                  value={
                    typeof rowData?.users === "number" ? String(rowData.users) : "-"
                  }
                />
                <Row
                  label="Application Users"
                  value={
                    typeof rowData?.application_users === "number"
                      ? String(rowData.application_users)
                      : "-"
                  }
                />
              </div>

              {/* Facilities as chips */}
              <div>
                <div className="text-sm text-gray-600 mb-1">Facilities</div>
                {hasFacilities ? (
                  <div className="flex flex-wrap gap-2">
                    {facilitiesKeys.map((k) => (
                      <span
                        key={k}
                        className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700"
                      >
                        {FACILITY_MAP[k] || k}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm font-medium text-gray-900">-</div>
                )}
              </div>

              {/* Pricing & Duration */}
              <div className="grid grid-cols-1 gap-4">
                <Row label="Price" value={priceText} />
                <Row label="Duration" value={`${durationLabel}${durationDays}`} />
              </div>

              {/* Timestamps */}
              <div className="pt-2 text-xs text-gray-500">
                {rowData?.createdAt && (
                  <div>Created: {new Date(rowData.createdAt).toLocaleString()}</div>
                )}
                {rowData?.updatedAt && (
                  <div>Updated: {new Date(rowData.updatedAt).toLocaleString()}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/** small helper for consistent rows */
const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="text-sm text-gray-600">{label}</span>
    <span className="text-sm font-medium text-gray-900 max-w-[70%] text-right break-all">
      {value || "-"}
    </span>
  </div>
);

export default PackagesView;
